import { useId } from 'react';
import type { Bank } from './banks';

/**
 * The drawings that are not Material icons — those are `./material`, lifted from the
 * font the phone draws them from. These are the phone's own `CustomPainter`s,
 * redrawn as SVG from the same numbers.
 */

export type NavGlyph = 'booking' | 'orders' | 'addresses' | 'profile';

/**
 * The tab bar's four outline glyphs — `nav_icons.dart`, in its 24-unit box at 22px:
 * a car from the front for «Заказ», the driver bar's list for «Поездки», a pin for
 * «Адреса», a head and shoulders for «Профиль». The selected tab's line is 0.4
 * heavier, because an outline this thin carries too little ink for colour alone.
 */
export function NavIcon({ glyph, on }: { glyph: NavGlyph; on: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth={on ? 2.3 : 1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyph === 'booking' && (
        <>
          <path d="M5 10 6.5 5.5Q7 4 8.5 4h7q1.5 0 2 1.5L19 10" />
          <rect x="4" y="10" width="16" height="8" rx="2" />
          <path d="M6 18v2M18 18v2M7 13.5h2M15 13.5h2" />
        </>
      )}
      {glyph === 'orders' && (
        <>
          <rect x="5" y="4" width="14" height="16" rx="2.5" />
          <path d="M8.5 8.7h7M8.5 12h7M8.5 15.3h4.2" />
        </>
      )}
      {glyph === 'addresses' && (
        <>
          <path d="M12 21s7-5.6 7-11A7 7 0 1 0 5 10c0 5.4 7 11 7 11z" />
          <circle cx="12" cy="10" r="2.5" />
        </>
      )}
      {glyph === 'profile' && (
        <>
          <circle cx="12" cy="7.75" r="3.75" />
          <path d="M4.5 20.5c0-3.6 3.4-5.8 7.5-5.8s7.5 2.2 7.5 5.8" />
        </>
      )}
    </svg>
  );
}

export type ActionGlyph = 'chat' | 'call' | 'refuse' | 'comment';

/**
 * The glyphs on an order card's pills and its comment — `action_icons.dart`, the
 * driver app's own traced from the design's SVGs, in `currentColor`: the bubble of
 * «Чат», the handset of «Звонок», the cross of «Отменить» (the driver's «Отказ»), and the bubble with two
 * lines of writing in it that marks what the passenger wrote. Each in its own viewBox,
 * scaled to [size], so the stroke thins and thickens with the glyph as on the phone.
 */
export function ActionIcon({ glyph, size }: { glyph: ActionGlyph; size: number }) {
  const box = glyph === 'refuse' ? 14 : 20;
  return (
    <svg
      viewBox={`0 0 ${box} ${box}`}
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={glyph === 'call' ? 1.7 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: 'none' }}
    >
      {glyph === 'chat' && (
        <path d="M17.2 9.6c0 3.4-3.2 6.2-7.2 6.2-.9 0-1.7-.1-2.5-.4l-4 1.3 1.3-3.4A5.8 5.8 0 0 1 2.8 9.6C2.8 6.2 6 3.4 10 3.4s7.2 2.8 7.2 6.2z" />
      )}
      {glyph === 'call' && (
        <path d="M6.4 3.2 8 6.1 6.5 7.9c.7 1.6 2 2.9 3.6 3.6l1.8-1.5 2.9 1.6-.3 2.7c-.1.7-.7 1.2-1.4 1.1-5.2-.5-9.3-4.6-9.8-9.8-.1-.7.4-1.3 1.1-1.4l2-.2z" />
      )}
      {glyph === 'refuse' && <path d="M1.8 1.8 12.2 12.2M12.2 1.8 1.8 12.2" />}
      {glyph === 'comment' && <path d="M3 3.4h14v9.8H7.2L3 16.8zM6.5 7.5h6.7M6.5 10.1h4" />}
    </svg>
  );
}

/**
 * The message on the phone in «Подтвердите номер»'s picture: a rounded box whose
 * tail runs down to the left in the same line, and three dots in it, in
 * `currentColor`. Drawn from the origin, 64 across and 55 down — the
 * same shapes as `_paintMessage` in the mobile apps.
 */
export function SmsMessage({ stroke }: { stroke: number }) {
  return (
    <>
      <path
        d="M4.9 40A28 23 0 0 1 0 27v-4A28 23 0 0 1 28 0h8a28 23 0 0 1 28 23v4a28 23 0 0 1-28 23h-8a28 23 0 0 1-9.6-1.4L2.5 55.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinejoin="round"
      />
      <g fill="currentColor">
        <circle cx="18" cy="25" r="4" />
        <circle cx="31" cy="25" r="4" />
        <circle cx="44" cy="25" r="4" />
      </g>
    </>
  );
}

/**
 * «ozon банк» in two lines, in a box of 328×194: the path the launcher icon of Ozon
 * Банк 19.31.0 draws it with. The mobile app's `bank_mark.dart` has the same one.
 */
const OZON_NAME = [
  'M81.27,38.22c0,22.6 -16.77,38.22 -39.65,38.22 -22.87,0 -39.65,-15.59',
  '-39.65,-38.22C1.98,15.59 18.75,0 41.63,0c22.89,0 39.65,15.59 39.65,38.22Z',
  'M236.68,38.22c0,22.6 -16.77,38.22 -39.65,38.22 -22.87,0 -39.65,-15.59',
  '-39.65,-38.22C157.39,15.59 174.16,0 197.04,0c22.88,0 39.65,15.59 39.65,38.22Z',
  'M318.9,29.84v44.08h-19.25L299.65,33.91c0,-11.53 -5.45,-16.27 -15.17,-16.27 -10.2,0',
  '-17.22,5.42',
  '-17.22,21.03v35.28h-19.25L248.01,2.27h19.02L267.03,13.12h0.45C272.23,4.99 280.4,0',
  '291.28,0c16.08,0 27.63,8.6 27.63,29.84Z',
  'M246.91,119.61v71.67h-19.25v-28.94h-29v28.94h-19.25v-71.67h19.25v26h29v-26h19.25Z',
  'M145.42,119.61v10.4h-0.45c-3.4,-7.24 -12.45,-12.67 -24.02,-12.67 -18.8,0',
  '-33.97,13.57 -33.97,37.97 0,24.65 14.72,38.44 33.97,38.44 11.77,0 20.17,-5.42',
  '24.02,-12.2h0.45v9.73h19.25v-71.67h-19.25Z M45.07,118.93c-11.77,0 -20.85,5.42',
  '-25.6,14.02h-0.45c0.68,-15.82 6.12,-22.15',
  '24.7,-23.97l28.77,-2.72L72.49,87.5l-31.94,2.49C9.3,92.49 0,113.74 0,147.65c0,30.06',
  '17.45,46.13 40.1,46.13 22.42,0 39.42,-16.04 39.42,-38.44 -0.02,-22.38 -15.42,-36.4',
  '-34.44,-36.4Z',
  'M88.3,2.27v16.27h38.74L87.4,58.55v15.37h65.24v-16.27h-41.45l39.65,-40.24L150.84,2.27L88.3,2.27Z',
  'M125.94,176.36c-11.55,0 -19.02,-8.58 -19.02,-20.58 0,-12.2 7.47,-21.03 19.02,-21.03',
  '11.55,0 19.48,8.59 19.48,20.81 0,11.98 -7.93,20.81 -19.48,20.81Z',
  'M22.15,38.22c0,12.43 7.7,20.58 19.48,20.58 11.78,0 19.48,-8.13 19.48,-20.58 0,-12.44',
  '-7.7,-20.58 -19.48,-20.58 -11.77,0 -19.48,8.14 -19.48,20.58Z M177.56,38.22c0,12.43',
  '7.7,20.58 19.48,20.58 11.77,0 19.48,-8.13 19.48,-20.58 0,-12.44 -7.7,-20.58',
  '-19.48,-20.58 -11.77,0 -19.48,8.14 -19.48,20.58Z M39.85,176.36c-11.1,0 -19.7,-8.36',
  '-19.7,-20.81 0,-12.65 8.6,-20.13 19.7,-20.13s19.7,7.46 19.7,20.13c0.02,12.43',
  '-8.6,20.81 -19.7,20.81Z M305.13,191.28',
  'L280.88,156.92v34.36h-19.25v-71.67h19.25v29.61l22.65,-29.61h22.2l-26.5,32.56L328,191.28h-22.88Z',
].join(' ');

/**
 * A bank's round badge on «Через приложение банка»: its colour and its mark in a few
 * strokes — or, for Sber, its ring, for Т-Банк, its shield, and for Ozon Банк, its
 * name. The banks' colours rather than the app's, the same in the dark theme, and
 * the same shapes the mobile app paints in `bank_mark.dart`, in a 1×1 box ×100.
 */
export function BankMark({ bank, size = 28 }: { bank: Bank; size?: number }) {
  // Sber's gradients are found by id, which has to be the page's only one of it.
  const id = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      {bank === 'sber' && (
        <>
          {/* A ring of its own rather than a sign on a disc: open at the top right
              where the tick runs out of it, and shaded round from green through
              yellow to blue. SVG has no sweep gradient, so the ring is six arcs,
              each shaded from one colour to the next. Its two ends are not cut
              square but along the tick, so the arcs run a little past both and
              the band the tick lies in is clipped out. Measured off the bank's
              own logo. */}
          <defs>
            <linearGradient id={`${id}-1`} gradientUnits="userSpaceOnUse" x1="72.1" y1="88.3" x2="23.6" y2="85.5">
              <stop offset="0" stopColor="#21A038" />
              <stop offset="1" stopColor="#EDE51B" />
            </linearGradient>
            <linearGradient id={`${id}-2`} gradientUnits="userSpaceOnUse" x1="23.6" y1="85.5" x2="5.8" y2="49.4">
              <stop offset="0" stopColor="#EDE51B" />
              <stop offset="1" stopColor="#93C83E" />
            </linearGradient>
            <linearGradient id={`${id}-3`} gradientUnits="userSpaceOnUse" x1="5.8" y1="49.4" x2="14.1" y2="24.1">
              <stop offset="0" stopColor="#93C83E" />
              <stop offset="1" stopColor="#1BB1A5" />
            </linearGradient>
            <linearGradient id={`${id}-4`} gradientUnits="userSpaceOnUse" x1="14.1" y1="24.1" x2="35.4" y2="8.2">
              <stop offset="0" stopColor="#1BB1A5" />
              <stop offset="1" stopColor="#13A3E0" />
            </linearGradient>
            <linearGradient id={`${id}-5`} gradientUnits="userSpaceOnUse" x1="35.4" y1="8.2" x2="82.9" y2="20.4">
              <stop offset="0" stopColor="#13A3E0" />
              <stop offset="1" stopColor="#0B78C8" />
            </linearGradient>
            <clipPath id={`${id}-cut`}>
              <path clipRule="evenodd" d="M-100-100H200V200H-100z M50 34.4 120-16.7 120 25.5 50 76.6z" />
            </clipPath>
          </defs>
          <g fill="none" strokeWidth="11.5" clipPath={`url(#${id}-cut)`}>
            <path d="M91.6 34.9A44.25 44.25 0 0 1 72.1 88.3" stroke="#21A038" />
            <path d="M72.1 88.3A44.25 44.25 0 0 1 23.6 85.5" stroke={`url(#${id}-1)`} />
            <path d="M23.6 85.5A44.25 44.25 0 0 1 5.8 49.4" stroke={`url(#${id}-2)`} />
            <path d="M5.8 49.4A44.25 44.25 0 0 1 14.1 24.1" stroke={`url(#${id}-3)`} />
            <path d="M14.1 24.1A44.25 44.25 0 0 1 35.4 8.2" stroke={`url(#${id}-4)`} />
            <path d="M35.4 8.2A44.25 44.25 0 0 1 82.9 20.4" stroke={`url(#${id}-5)`} />
          </g>
          <path d="M31 37.2 50 48.1 89.6 19.3 95.5 29.1 49.7 62.8 31 50.5z" fill="#21A038" />
        </>
      )}
      {bank === 'tbank' && (
        // A shield of its own rather than a sign on a disc, as wide as it is tall:
        // the path the monochrome launcher icon of Т-Банк 8.4.2 draws it with, the
        // T cut out of the shield there and laid over it here.
        <svg viewBox="25.78 29.81 56.44 56.44" width="100" height="100">
          <path d="M82.22,58.2C82.22,65.48 78.34,72.21 72.04,75.84L54,86.25L35.96,75.84C29.66,72.2 25.78,65.48 25.78,58.2V29.81H82.22V58.2Z" fill="#FFDD2D" />
          <path d="M41.27,53.58C42.48,52.22 44.67,51.3 47.19,51.3H49.92V61.56C49.92,64.29 49.17,66.68 48.07,68H59.94C58.84,66.68 58.09,64.3 58.09,61.57V51.3H60.82C63.33,51.3 65.53,52.23 66.74,53.58V44.75H41.27V53.58Z" fill="#333" />
        </svg>
      )}
      {bank === 'vtb' && (
        <>
          <circle cx="50" cy="50" r="50" fill="#0B5FD6" />
          {/* Three bars, leaning like the letters of the name. */}
          <g fill="#fff">
            <path d="M39 30h35l-3 11H36z" />
            <path d="M34 44.5h35l-3 11H31z" />
            <path d="M29 59h35l-3 11H26z" />
          </g>
        </>
      )}
      {bank === 'ozon' && (
        <>
          <circle cx="50" cy="50" r="50" fill="#005BFF" />
          {/* The bank's mark is its name, as wide as a disc lets two lines of it be. */}
          <path d={OZON_NAME} fill="#fff" transform="translate(12 27.52) scale(.2317)" />
        </>
      )}
    </svg>
  );
}
